/**
 * XLSX Export Utility
 *
 * Shared utility for exporting data to .xlsx files.
 * Uses SheetJS (xlsx) library for workbook generation.
 *
 * @example
 * import { exportToXlsx } from '@spcc/core'
 *
 * exportToXlsx({
 *   fileName: 'orders',
 *   sheetName: 'Orders',
 *   columns: [
 *     { header: 'Order #', key: 'orderNumber' },
 *     { header: 'Status', key: 'status' },
 *   ],
 *   rows: orders,
 * })
 */

import * as XLSX from 'xlsx-js-style'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

export interface ExportColumn<T = Record<string, unknown>> {
  /** Column header label */
  header: string
  /** Property key on the data object, or a custom accessor */
  key?: keyof T & string
  /** Custom value accessor (overrides key) */
  accessor?: (row: T, index: number) => string | number | boolean | null | undefined
  /** Column width in characters (optional) */
  width?: number
}

export interface ExportToXlsxOptions<T = Record<string, unknown>> {
  /** File name without extension */
  fileName: string
  /** Sheet name (defaults to 'Sheet1') */
  sheetName?: string
  /** Column definitions */
  columns: ExportColumn<T>[]
  /** Data rows */
  rows: T[]
}

export const MOBILE_XLSX_EXPORT_READY_EVENT = 'spcc:mobile-xlsx-export-ready'

export interface MobileXlsxExportPreview {
  headers: string[]
  rows: Array<Array<string | number | boolean | null>>
}

export interface MobileXlsxExportReadyDetail {
  fileName: string
  sheetName: string
  fileUri: string
  previewUrl: string
  fileSizeBytes: number
  preview: MobileXlsxExportPreview
}

function getCellValue<T>(
  row: T,
  column: ExportColumn<T>,
  index: number
): string | number | boolean | null | undefined {
  if (column.accessor) return column.accessor(row, index)
  if (column.key) return (row as Record<string, unknown>)[column.key] as string | number | boolean | null | undefined
  return ''
}

function isNativePlatform(): boolean {
  return typeof Capacitor?.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
}

function getCapacitorPlatform(): string {
  if (typeof Capacitor?.getPlatform === 'function') {
    return Capacitor.getPlatform()
  }
  return 'web'
}

function getExportDateStamp(): string {
  return new Date().toISOString().split('T')[0]
}

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim()
}

function ensureXlsxFilename(name: string): string {
  const trimmed = sanitizeFilename(name)
  if (!trimmed) return `export-${getExportDateStamp()}.xlsx`
  return trimmed.toLowerCase().endsWith('.xlsx') ? trimmed : `${trimmed}.xlsx`
}

function normalizeFileUri(uri: string): string {
  if (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('http')) {
    return uri
  }
  if (uri.startsWith('/')) {
    return `file://${uri}`
  }
  return uri
}

async function tryWriteFile(base64: string, filename: string, directory: Directory): Promise<string> {
  const result = await Filesystem.writeFile({
    directory,
    path: filename,
    data: base64,
    recursive: true,
  })
  return normalizeFileUri(result.uri)
}

function getNativeWriteDirectories(platform: string): Directory[] {
  if (platform === 'android') {
    return [Directory.Cache, Directory.Data, Directory.ExternalCache, Directory.External]
  }
  return [Directory.Cache, Directory.Documents]
}

async function writeXlsxToNativeFilesystem(base64: string, filename: string): Promise<string> {
  const platform = getCapacitorPlatform()
  const directories = getNativeWriteDirectories(platform)

  for (const directory of directories) {
    try {
      return await tryWriteFile(base64, filename, directory)
    } catch {
      // Try next directory
    }
  }

  throw new Error('Unable to write xlsx file on native device')
}

function buildPreviewRows(
  rows: Array<Array<string | number | boolean | null | undefined>>,
  maxRows = 200
): Array<Array<string | number | boolean | null>> {
  const sliced = rows.slice(0, maxRows)
  return sliced.map((row) => row.map((value) => (value ?? null)))
}

function dispatchMobileExportReady(detail: MobileXlsxExportReadyDetail): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<MobileXlsxExportReadyDetail>(MOBILE_XLSX_EXPORT_READY_EVENT, { detail }))
}

async function exportToNativeXlsx(params: {
  workbook: XLSX.WorkBook
  fileName: string
  sheetName: string
  headers: string[]
  data: Array<Array<string | number | boolean | null | undefined>>
}): Promise<void> {
  const { workbook, fileName, sheetName, headers, data } = params

  const finalFilename = ensureXlsxFilename(fileName)
  const base64 = XLSX.write(workbook, { type: 'base64', bookType: 'xlsx' })
  const fileUri = await writeXlsxToNativeFilesystem(base64, finalFilename)
  const previewUrl = typeof Capacitor?.convertFileSrc === 'function' ? Capacitor.convertFileSrc(fileUri) : fileUri
  const fileSizeBytes = Math.floor((base64.length * 3) / 4)

  dispatchMobileExportReady({
    fileName: finalFilename,
    sheetName,
    fileUri,
    previewUrl,
    fileSizeBytes,
    preview: {
      headers,
      rows: buildPreviewRows(data),
    },
  })
}

function exportToWebXlsx(workbook: XLSX.WorkBook, fileName: string): void {
  XLSX.writeFile(workbook, ensureXlsxFilename(fileName))
}

/**
 * Exports data to an .xlsx file and triggers browser download.
 */
export function exportToXlsx<T>(options: ExportToXlsxOptions<T>): void {
  void exportToXlsxInternal(options)
}

async function exportToXlsxInternal<T>(options: ExportToXlsxOptions<T>): Promise<void> {
  const { fileName, sheetName = 'Sheet1', columns, rows } = options

  const headers = columns.map(c => c.header)
  const data = rows.map((row, index) =>
    columns.map(col => {
      const value = getCellValue(row, col, index)
      return value ?? ''
    })
  )

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])

  // Apply column widths
  worksheet['!cols'] = columns.map(col => ({
    wch: col.width ?? Math.max(col.header.length + 2, 14),
  }))

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

  const fileNameWithDate = `${fileName}-${getExportDateStamp()}`

  if (!isNativePlatform()) {
    exportToWebXlsx(workbook, fileNameWithDate)
    return
  }

  try {
    await exportToNativeXlsx({
      workbook,
      fileName: fileNameWithDate,
      sheetName,
      headers,
      data,
    })
  } catch (error) {
    console.error('[exportToXlsx] Native export failed, using web download fallback', error)
    exportToWebXlsx(workbook, fileNameWithDate)
  }
}

// --- Multi-Sheet Export with Styling ---

export interface SheetDefinition<T = Record<string, unknown>> {
  sheetName: string
  columns: ExportColumn<T>[]
  rows: T[]
}

export interface ExportToXlsxMultiSheetOptions {
  fileName: string
  sheets: SheetDefinition<any>[]
  /** Apply header styling (bold, background color) */
  headerStyle?: XLSX.CellStyle
}

const DEFAULT_HEADER_STYLE: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: 'FFFFFF' } },
  fill: { fgColor: { rgb: '4472C4' }, patternType: 'solid' },
  alignment: { horizontal: 'center', vertical: 'center' },
  border: {
    bottom: { style: 'thin', color: { rgb: '000000' } },
  },
}

function applyHeaderStyle(ws: XLSX.WorkSheet, colCount: number, style: XLSX.CellStyle): void {
  for (let c = 0; c < colCount; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c })
    if (ws[ref]) ws[ref].s = style
  }
}

/**
 * Exports data to a single .xlsx file with multiple sheets and optional styling.
 */
export function exportToXlsxMultiSheet(options: ExportToXlsxMultiSheetOptions): void {
  void exportToXlsxMultiSheetInternal(options)
}

async function exportToXlsxMultiSheetInternal(options: ExportToXlsxMultiSheetOptions): Promise<void> {
  const { fileName, sheets, headerStyle = DEFAULT_HEADER_STYLE } = options

  const workbook = XLSX.utils.book_new()

  for (const sheet of sheets) {
    const headers = sheet.columns.map((c) => c.header)
    const data = sheet.rows.map((row, index) =>
      sheet.columns.map((col) => getCellValue(row, col, index) ?? '')
    )

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])

    worksheet['!cols'] = sheet.columns.map((col) => ({
      wch: col.width ?? Math.max(col.header.length + 2, 14),
    }))

    applyHeaderStyle(worksheet, headers.length, headerStyle)

    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.sheetName)
  }

  const fileNameWithDate = `${fileName}-${getExportDateStamp()}`

  if (!isNativePlatform()) {
    exportToWebXlsx(workbook, fileNameWithDate)
    return
  }

  try {
    const firstSheet = sheets[0]
    await exportToNativeXlsx({
      workbook,
      fileName: fileNameWithDate,
      sheetName: firstSheet?.sheetName ?? 'Sheet1',
      headers: firstSheet?.columns.map((c) => c.header) ?? [],
      data: firstSheet?.rows.map((row, index) =>
        firstSheet.columns.map((col) => getCellValue(row, col, index) ?? '')
      ) ?? [],
    })
  } catch (error) {
    console.error('[exportToXlsxMultiSheet] Native export failed, using web download fallback', error)
    exportToWebXlsx(workbook, fileNameWithDate)
  }
}
